import { useEffect, useMemo, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  adminListWaitlist,
  adminMe,
  adminExportCsvUrl,
  adminDeleteEntry,
  getAdminToken,
  setAdminToken,
} from "@/lib/api";
import {
  Search,
  Download,
  LogOut,
  RefreshCw,
  Trash2,
  Users,
  Star,
  Tag as TagIcon,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const TagPill = ({ tag }) => {
  if (tag === "founding") {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-[#B87CFF]/20 text-[#B87CFF] border border-[#B87CFF]/30 px-2 py-0.5 rounded-full"
        data-testid={`tag-founding`}
      >
        <Star className="w-2.5 h-2.5" /> Founding
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] bg-[#F4ECFF]/10 text-[#F4ECFF]/70 border border-[#F4ECFF]/20 px-2 py-0.5 rounded-full"
      data-testid={`tag-standard`}
    >
      <TagIcon className="w-2.5 h-2.5" /> Standard
    </span>
  );
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListWaitlist({ search, tag });
      setItems(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAdminToken(null);
        setAuthed(false);
      } else {
        toast.error("Failed to load waitlist.");
      }
    } finally {
      setLoading(false);
    }
  }, [search, tag]);

  useEffect(() => {
    if (!getAdminToken()) {
      setAuthChecked(true);
      return;
    }
    adminMe()
      .then((m) => {
        setMe(m);
        setAuthed(true);
      })
      .catch(() => {
        setAdminToken(null);
        setAuthed(false);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    if (authed) {
      const t = setTimeout(fetchAll, search ? 250 : 0);
      return () => clearTimeout(t);
    }
  }, [authed, fetchAll, search]);

  const stats = useMemo(() => {
    const founding = items.filter((i) => i.tag === "founding").length;
    const standard = items.filter((i) => i.tag === "standard").length;
    const completed = items.filter((i) => i.details_completed).length;
    return { total: items.length, founding, standard, completed };
  }, [items]);

  const logout = () => {
    setAdminToken(null);
    navigate("/admin/login");
  };

  const handleExport = () => {
    const token = getAdminToken();
    fetch(adminExportCsvUrl(tag), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("export failed");
        return r.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `feminineways-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("CSV downloaded.");
      })
      .catch(() => toast.error("CSV export failed."));
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete ${entry.email}? This cannot be undone.`)) return;
    try {
      await adminDeleteEntry(entry.id);
      setItems((s) => s.filter((i) => i.id !== entry.id));
      toast.success("Entry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#0B0A4A] text-[#F4ECFF] flex items-center justify-center">
        <RefreshCw className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (!authed) return <Navigate to="/admin/login" replace />;

  return (
    <main className="min-h-screen bg-[#0B0A4A] text-[#F4ECFF] font-sans" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[#F4ECFF]/10 backdrop-blur-md bg-[#0B0A4A]/80 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_feminineways-notify/artifacts/vvjjpevv_FeminineWays%20logo.png"
              alt="Feminine Ways"
              className="w-9 h-9 rounded-lg"
            />
            <div>
              <p className="font-serif text-lg leading-none">
                Feminine<span className="text-[#B87CFF]">Ways</span>
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4ECFF]/55 mt-0.5">
                Admin · Waitlist
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-[#F4ECFF]/60" data-testid="admin-email">
              {me?.email}
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm text-[#F4ECFF]/80 hover:text-[#B87CFF] transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        {/* Stat cards */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-5" data-testid="admin-stats">
          <StatCard label="Total signups" value={stats.total} icon={Users} />
          <StatCard label="Founding rate" value={stats.founding} icon={Star} accent />
          <StatCard label="Standard" value={stats.standard} icon={TagIcon} />
          <StatCard label="Profile complete" value={stats.completed} icon={CheckCircle2} />
        </section>

        {/* Controls */}
        <section className="mt-8 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#F4ECFF]/50" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email, name, source…"
              className="w-full rounded-full bg-white/5 border border-white/15 text-[#F4ECFF] placeholder-[#F4ECFF]/45 pl-11 pr-5 py-3 outline-none focus:ring-2 focus:ring-[#B87CFF]/40"
              data-testid="admin-search-input"
            />
          </div>

          <div className="flex items-center gap-2" data-testid="admin-filter-tags">
            {[
              { v: "", label: "All" },
              { v: "founding", label: "Founding" },
              { v: "standard", label: "Standard" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setTag(opt.v)}
                className={`text-xs px-4 py-2.5 rounded-full border transition-colors ${
                  tag === opt.v
                    ? "bg-[#B87CFF] text-[#0B0A4A] border-[#B87CFF]"
                    : "bg-white/5 border-white/15 text-[#F4ECFF]/80 hover:border-[#B87CFF]/40"
                }`}
                data-testid={`admin-filter-${opt.v || "all"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <button
            onClick={fetchAll}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/15 text-[#F4ECFF] px-4 py-2.5 text-xs hover:border-[#B87CFF]/40 transition-colors"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>

          <button
            onClick={handleExport}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#B87CFF] hover:bg-[#F4ECFF] text-[#0B0A4A] px-5 py-2.5 text-xs font-medium tracking-wide transition-colors"
            data-testid="admin-export-btn"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </section>

        {/* Table */}
        <section className="mt-6 rounded-3xl border border-[#F4ECFF]/10 bg-[#F4ECFF]/[0.03] overflow-hidden" data-testid="admin-table-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-waitlist-table">
              <thead className="bg-[#F4ECFF]/5 text-[10px] uppercase tracking-[0.18em] text-[#F4ECFF]/55">
                <tr>
                  <th className="text-left px-5 py-4">Joined</th>
                  <th className="text-left px-5 py-4">Tag</th>
                  <th className="text-left px-5 py-4">Email</th>
                  <th className="text-left px-5 py-4">Name</th>
                  <th className="text-left px-5 py-4">Platform</th>
                  <th className="text-left px-5 py-4">Use case</th>
                  <th className="text-left px-5 py-4">Source</th>
                  <th className="text-left px-5 py-4">Profile</th>
                  <th className="text-right px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F4ECFF]/10">
                {loading && items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-[#F4ECFF]/55">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-[#F4ECFF]/55" data-testid="admin-empty-state">
                      No signups match your filters yet.
                    </td>
                  </tr>
                )}
                {items.map((entry) => (
                  <tr key={entry.id} className="hover:bg-[#F4ECFF]/[0.04]" data-testid={`admin-row-${entry.id}`}>
                    <td className="px-5 py-3 text-[#F4ECFF]/80 whitespace-nowrap">{fmtDate(entry.created_at)}</td>
                    <td className="px-5 py-3"><TagPill tag={entry.tag} /></td>
                    <td className="px-5 py-3 text-[#F4ECFF] font-medium">{entry.email}</td>
                    <td className="px-5 py-3 text-[#F4ECFF]/80">
                      {[entry.first_name, entry.last_name].filter(Boolean).join(" ") || <span className="text-[#F4ECFF]/40">—</span>}
                    </td>
                    <td className="px-5 py-3 text-[#F4ECFF]/80 capitalize">
                      {entry.platform || <span className="text-[#F4ECFF]/40">—</span>}
                    </td>
                    <td className="px-5 py-3 text-[#F4ECFF]/80 max-w-[220px] truncate">
                      {entry.use_case || <span className="text-[#F4ECFF]/40">—</span>}
                    </td>
                    <td className="px-5 py-3 text-[#F4ECFF]/80">
                      {entry.referral_source || entry.source || <span className="text-[#F4ECFF]/40">—</span>}
                    </td>
                    <td className="px-5 py-3">
                      {entry.details_completed ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#F4ECFF]/45">
                          <XCircle className="w-3.5 h-3.5" /> Email only
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => handleDelete(entry)}
                        className="text-[#F4ECFF]/50 hover:text-red-400 transition-colors p-1"
                        data-testid={`admin-delete-${entry.id}`}
                        aria-label={`Delete ${entry.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <p className="text-center text-[10px] uppercase tracking-[0.22em] text-[#F4ECFF]/35 mt-10">
          Founding rate window closes August 1, 2026
        </p>
      </div>
    </main>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div
      className={`rounded-2xl border p-5 ${
        accent
          ? "bg-[#B87CFF]/10 border-[#B87CFF]/30"
          : "bg-[#F4ECFF]/[0.04] border-[#F4ECFF]/10"
      }`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4ECFF]/60">{label}</p>
        <Icon className={`w-4 h-4 ${accent ? "text-[#B87CFF]" : "text-[#F4ECFF]/40"}`} strokeWidth={1.75} />
      </div>
      <p className={`font-serif text-3xl mt-2 ${accent ? "text-[#F4ECFF]" : "text-[#F4ECFF]"}`}>{value}</p>
    </div>
  );
}
